import React, { useRef } from 'react';
import { motion, useInView } from "motion/react";
import { getSignedMediaUrl } from '@/lib/utils';
import { SocialIcon } from '@/features/artists/components/SocialIcons';
import type { Business } from '@/features/clubs/types/types';

interface ClubFooterProps {
  clubData: Business;
}

const ClubFooter: React.FC<ClubFooterProps> = ({ clubData }) => {
  const footerRef = useRef(null);
  const isInView = useInView(footerRef, { once: true, amount: 0.2 });
  const { name, social_media_links, partnerships } = clubData;

  const legalLinks = [
    "PRIVACY PLATFORM", 
    "LEGAL WARNING", 
    "PRIVACY POLICY", 
    "COOKIES POLICY", 
    "CONTACT", 
    "HELP CENTER"
  ];

  return (
    <motion.footer
      ref={footerRef}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 1 }}
      className="bg-black text-white relative z-10"
    >
      {/* Top Separator */}
      <div className="border-t border-gray-800/50"></div>
      
      <div className="max-w-7xl mx-auto px-8 pt-20 pb-10 flex flex-col items-center text-center">
        {/* Social Media Links */}
        {social_media_links && (
          <div className="flex items-center gap-x-6 mb-8">
            {Object.entries(social_media_links).map(([platform, url]) => (
              <a
                key={platform}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors duration-300"
                aria-label={platform}
              >
                <SocialIcon platform={platform} />
              </a>
            ))}
          </div>
        )}
        
        {/* Legal Links Navigation */}
        <nav className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 mb-16">
          {legalLinks.map(link => (
            <a 
              key={link} 
              href="#" 
              className="text-sm font-medium tracking-wider text-gray-400 hover:text-white transition-colors duration-300 uppercase"
            >
              {link}
            </a>
          ))}
        </nav>

        {/* Partnership Logos */}
        {partnerships && partnerships.length > 0 && (
          <div className="flex flex-wrap justify-center items-center gap-x-12 sm:gap-x-16 gap-y-8 mb-16">
            {partnerships.map((partner) => (
              <a 
                key={partner.logo} 
                href={partner.website} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="opacity-80 hover:opacity-100 transition-opacity h-12 w-32"
              >
                <img
                  src={getSignedMediaUrl(partner.logo, 'image').url}
                  alt={`${partner.logo} logo`}
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    // Fallback to a placeholder if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `
                      <div class="h-full w-full flex items-center justify-center bg-gray-800 text-gray-500 text-xs tracking-widest uppercase">
                        ${partner.logo}
                      </div>
                    `;
                  }}
                />
              </a>
            ))}
          </div>
        )}

        {/* Copyright */}
        <p className="text-sm tracking-widest text-gray-500 uppercase">
          © {new Date().getFullYear()} {name}
        </p>
      </div>

      <div className="border-t border-gray-800/50">
        {/* Additional footer content can be added here if needed */}
      </div>
    </motion.footer>
  );
};

export default ClubFooter;
